import { useMemo, useRef, useCallback } from "react";
import { makeStyles } from "@fluentui/react-components";
import { FilePdf, FileDoc, FileXls, FilePpt, FileHtml, FileText, File, Link } from "@phosphor-icons/react";
import DOMPurify from "dompurify";

import type { ChatResponse } from "../../store/types/chatTypes";
import { parseCitation } from "../../store/selectors/chatSelectors";
import { parseAnswerToHtml } from "./AnswerParser";
import UserRating from "../UserRating/UserRating";
import { tokens } from "../../theme";
import { brand } from "@brand";

// =============================================================================
// TOKEN SHORTCUTS
// =============================================================================

const t = {
    // Spacing
    spacingVerticalS: tokens.primitives["spacing-Vertical-S"],
    spacingVerticalM: tokens.primitives["spacing-Vertical-M"],
    spacingVerticalL: tokens.primitives["spacing-Vertical-L"],
    spacingHorizontalS: tokens.primitives["spacing-Horizontal-S"],
    spacingHorizontalM: tokens.primitives["spacing-Horizontal-M"],
    spacingHorizontalL: tokens.primitives["spacing-Horizontal-L"],

    // Typography
    fontFamilyBase: tokens.primitives["font-Family-Base"],
    fontSizeBase300: tokens.primitives["font-Size-Base-300"],
    fontSizeBase400: tokens.primitives["font-Size-Base-400"],
    fontWeightRegular: tokens.primitives["font-Weight-Regular"],
    fontWeightMedium: tokens.primitives["font-Weight-Medium"],
    fontWeightSemiBold: tokens.primitives["font-Weight-SemiBold"],
    lineHeightBase400: tokens.primitives["lineHeight-Base-400"],

    // Colors
    themePrimary: tokens.palette["theme-Primary"],
    themeDark: tokens.palette["theme-Dark"],
    themeDarkAlt: tokens.palette["theme-DarkAlt"],
    themeLighter: tokens.palette["theme-Lighter"],
    themeLighterAlt: tokens.palette["theme-LighterAlt"],
    neutralPrimary: tokens.semantic["neutral-Primary"],
    neutralSecondary: tokens.semantic["neutral-Secondary"],
    neutralWhite: tokens.semantic["neutral-White"],
    neutralLighter: tokens.semantic["neutral-Lighter"],
    neutralLighterAlt: tokens.semantic["neutral-LighterAlt"],
    secondaryBg: tokens.semantic["brandSecondaryBackground"],
    primaryStroke: tokens.semantic["brandPrimaryStroke"],
    secondaryFg: tokens.semantic["brandSecondaryForeground"],
    secondaryStroke: tokens.semantic["brandSecondaryStroke"],

    // Radius
    radiusSmall: tokens.primitives["stroke-Radius-Small"],
    radiusLarge: tokens.primitives["stroke-Radius-Large"],
    radiusXLarge: tokens.primitives["stroke-Radius-XLarge"],
    radiusXXLarge: tokens.primitives["stroke-RadiusXxl"],
    radiusCircular: tokens.primitives["stroke-Radius-Circular"],

    // Shadows
    shadowRestingSmall: tokens.shadows["shadow-Resting-Small"],
    shadowRestingXSmall: tokens.shadows["shadow-Resting-Xs"],

    // Stroke
    strokeFocus: tokens.primitives.strokeFocus
};

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
    wrapper: {
        display: "flex",
        justifyContent: "flex-start",
        width: "100%",

        "@media (max-width: 768px)": {
            paddingRight: "16px"
        }
    },

    container: {
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        gap: t.spacingHorizontalM,
        width: "100%",
        maxWidth: "850px",
        backgroundColor: t.secondaryBg,
        border: `1px solid ${t.primaryStroke}`,
        borderRadius: t.radiusLarge,
        padding: t.spacingHorizontalL,
        boxSizing: "border-box",
        boxShadow: t.shadowRestingXSmall,

        "@media (max-width: 768px)": {
            maxWidth: "none"
        }
    },

    iconContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "40px",
        height: "40px",
        borderRadius: t.radiusCircular,
        backgroundColor: t.secondaryFg,
        color: t.neutralWhite,
        flexShrink: 0,

        "@media (max-width: 768px)": {
            width: "24px",
            height: "24px"
        }
    },

    robotIcon: {
        "@media (max-width: 768px)": {
            width: "16px",
            height: "16px"
        }
    },

    botIcon: {
        filter: "brightness(0) invert(1)",
        "@media (max-width: 768px)": {
            width: "16px",
            height: "16px"
        }
    },

    content: {
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minWidth: 0,
        gap: t.spacingVerticalM
    },

    answerText: {
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase400,
        fontWeight: t.fontWeightRegular as unknown as number,
        lineHeight: t.lineHeightBase400,
        color: t.neutralPrimary,
        wordBreak: "break-word",
        overflowWrap: "break-word",

        "& p": {
            margin: 0,
            marginBottom: t.spacingVerticalS
        },

        "& p:last-child": {
            marginBottom: 0
        },

        // Inline citation link styles
        "& .citation-link": {
            color: t.themePrimary,
            textDecoration: "none",
            cursor: "pointer",
            fontWeight: t.fontWeightMedium as unknown as number,
            transitionProperty: "color",
            transitionDuration: "0.15s",

            "&:hover": {
                color: t.themeDarkAlt,
                textDecoration: "underline"
            },

            "&:focus-visible": {
                outline: `2px solid ${t.strokeFocus}`,
                outlineOffset: "1px",
                borderRadius: "2px"
            }
        },

        "& .citation-link sup": {
            fontSize: "0.75em",
            verticalAlign: "super",
            lineHeight: 0
        }
    },

    sectionHeader: {
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase400,
        fontWeight: t.fontWeightSemiBold as unknown as number,
        color: t.neutralPrimary,
        margin: 0,
        marginBottom: t.spacingVerticalS
    },

    citationsContainer: {
        display: "flex",
        flexDirection: "column",
        gap: t.spacingVerticalS
    },

    citationLink: {
        display: "flex",
        alignItems: "flex-start",
        gap: t.spacingHorizontalS,
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase300,
        fontWeight: t.fontWeightRegular as unknown as number,
        color: t.themePrimary,
        textDecoration: "none",
        transitionProperty: "color",
        transitionDuration: "0.15s",
        maxWidth: "100%",

        "&:hover": {
            color: t.themeDarkAlt,
            textDecoration: "underline"
        },

        "&:focus-visible": {
            outline: `2px solid ${t.strokeFocus}`,
            outlineOffset: "2px"
        }
    },

    citationText: {
        overflowWrap: "break-word",
        minWidth: 0
    },

    citationIcon: {
        color: t.themePrimary,
        flexShrink: 0
    },

    citationNumber: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: "16px",
        fontSize: t.fontSizeBase300,
        fontWeight: t.fontWeightMedium as unknown as number,
        color: t.themePrimary,
        flexShrink: 0
    },

    followupContainer: {
        display: "flex",
        flexDirection: "column",
        gap: t.spacingVerticalS
    },

    followupQuestion: {
        display: "block",
        backgroundColor: t.secondaryBg,
        border: `1px solid ${t.secondaryStroke}`,
        borderRadius: t.radiusSmall,
        padding: `${t.spacingVerticalS} ${t.spacingHorizontalM}`,
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase300,
        fontWeight: t.fontWeightRegular as unknown as number,
        color: t.themePrimary,
        textDecoration: "none",
        cursor: "pointer",
        transitionProperty: "border-color, background-color",
        transitionDuration: "0.15s",
        boxShadow: t.shadowRestingXSmall,
        textAlign: "left",

        "&:hover": {
            border: `1px solid ${t.themePrimary}`,
            backgroundColor: t.neutralLighterAlt
        },

        "&:focus-visible": {
            outline: `2px solid ${t.strokeFocus}`,
            outlineOffset: "2px"
        }
    },

    ratingContainer: {
        display: "flex",
        justifyContent: "flex-end",
        marginTop: t.spacingVerticalS
    },

    typingIndicator: {
        display: "flex",
        alignItems: "center",
        gap: "4px",
        padding: "8px 0 4px",
    },

    typingDot: {
        width: "6px",
        height: "6px",
        borderRadius: "50%",
        backgroundColor: t.neutralSecondary,
        animationName: {
            "0%, 80%, 100%": { opacity: 0.3, transform: "scale(0.8)" },
            "40%": { opacity: 1, transform: "scale(1)" },
        },
        animationDuration: "1.2s",
        animationIterationCount: "infinite",
        animationTimingFunction: "ease-in-out",
    },

    srOnly: {
        position: "absolute",
        width: "1px",
        height: "1px",
        padding: "0",
        margin: "-1px",
        overflow: "hidden",
        clipPath: "inset(50%)",
        whiteSpace: "nowrap",
        border: "0",
    },
});

// =============================================================================
// DOMPURIFY CONFIG
// =============================================================================

const DOMPURIFY_CONFIG = {
    ADD_ATTR: ["data-citation-path", "data-source-id", "role", "tabindex"],
    ADD_TAGS: [],
    ALLOWED_TAGS: [
        "a", "p", "br", "strong", "em", "ul", "ol", "li", "sup", "sub", "span",
        "h1", "h2", "h3", "h4", "h5", "h6",
        "table", "thead", "tbody", "tr", "th", "td",
        "code", "pre", "blockquote", "hr", "del",
    ],
    ALLOWED_ATTR: ["href", "title", "class", "data-citation-path", "data-source-id", "role", "tabindex"],
};

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Get the appropriate Phosphor icon based on file extension or URL type
 */
function getFileIcon(filename: string): React.ComponentType<{ size?: number; weight?: "fill" | "regular"; className?: string }> {
    const lowerName = filename.toLowerCase();

    // Check for URL patterns first
    if (lowerName.startsWith("http://") || lowerName.startsWith("https://")) {
        // Check if URL ends with a known file extension
        if (lowerName.endsWith(".pdf")) return FilePdf;
        if (lowerName.endsWith(".doc") || lowerName.endsWith(".docx")) return FileDoc;
        if (lowerName.endsWith(".xls") || lowerName.endsWith(".xlsx")) return FileXls;
        if (lowerName.endsWith(".ppt") || lowerName.endsWith(".pptx")) return FilePpt;
        if (lowerName.endsWith(".html") || lowerName.endsWith(".htm")) return FileHtml;
        if (lowerName.endsWith(".txt") || lowerName.endsWith(".md")) return FileText;
        // Default for URLs without recognizable extension
        return Link;
    }

    // Check file extensions
    if (lowerName.endsWith(".pdf")) return FilePdf;
    if (lowerName.endsWith(".doc") || lowerName.endsWith(".docx")) return FileDoc;
    if (lowerName.endsWith(".xls") || lowerName.endsWith(".xlsx")) return FileXls;
    if (lowerName.endsWith(".ppt") || lowerName.endsWith(".pptx")) return FilePpt;
    if (lowerName.endsWith(".html") || lowerName.endsWith(".htm")) return FileHtml;
    if (lowerName.endsWith(".txt") || lowerName.endsWith(".md")) return FileText;

    // Default icon
    return File;
}

/**
 * Insert zero-width spaces at natural break points for URLs/long strings
 * This helps browsers find good places to wrap without breaking mid-word
 */
function insertWordBreaks(text: string): string {
    // Insert zero-width space after these characters to suggest break points
    return text.replace(/([/._\-?&=%])/g, "$1\u200B");
}

// =============================================================================
// COMPONENT
// =============================================================================

interface Props {
    answer: ChatResponse;
    isSelected?: boolean;
    isStreaming: boolean;
    onCitationClicked: (filePath: string) => void;
    onFollowupQuestionClicked?: (question: string) => void;
    onFeedbackClicked?: (feedback: number) => void;
    onAdditionalFeedbackSubmitted?: (feedbackOptions: { option_id: number; option_text: string }[], userInput: string) => void;
    showFollowupQuestions?: boolean;
}

export const Answer = ({
    answer,
    isSelected: _isSelected,
    isStreaming,
    onCitationClicked,
    onFollowupQuestionClicked,
    onFeedbackClicked,
    onAdditionalFeedbackSubmitted,
    showFollowupQuestions
}: Props) => {
    const styles = useStyles();
    const answerTextRef = useRef<HTMLDivElement>(null);
    const messageContent = answer.answer;

    const sourceUrls = answer.source_urls;
    const parsedAnswer = useMemo(() => parseAnswerToHtml(messageContent, isStreaming, sourceUrls), [messageContent, isStreaming, sourceUrls]);

    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml, DOMPURIFY_CONFIG);

    // Event delegation for citation clicks
    const handleAnswerTextClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            const target = event.target as HTMLElement;
            const citationLink = target.closest(".citation-link");

            if (citationLink) {
                event.preventDefault();
                const citationPath = citationLink.getAttribute("data-citation-path");
                if (citationPath) {
                    onCitationClicked(citationPath);
                }
            }
        },
        [onCitationClicked]
    );

    // Handle keyboard navigation for citations
    const handleAnswerTextKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === "Enter" || event.key === " ") {
                const target = event.target as HTMLElement;
                const citationLink = target.closest(".citation-link");

                if (citationLink) {
                    event.preventDefault();
                    const citationPath = citationLink.getAttribute("data-citation-path");
                    if (citationPath) {
                        onCitationClicked(citationPath);
                    }
                }
            }
        },
        [onCitationClicked]
    );

    const handleFollowupClick = useCallback(
        (question: string) => {
            if (onFollowupQuestionClicked) {
                onFollowupQuestionClicked(question);
            }
        },
        [onFollowupQuestionClicked]
    );

    return (
        <article className={styles.wrapper} aria-label="Assistant response">
            <div className={styles.container}>
                <div className={styles.iconContainer} aria-hidden="true">
                    <img src={brand.avatar} alt="" width={28} height={28} className={styles.botIcon} />
                </div>

                <div className={styles.content}>
                    {/* Answer Text */}
                    <div
                        ref={answerTextRef}
                        className={styles.answerText}
                        // eslint-disable-next-line react/no-danger -- HTML is sanitized via DOMPurify
                        dangerouslySetInnerHTML={{ __html: sanitizedAnswerHtml }}
                        onClick={handleAnswerTextClick}
                        onKeyDown={handleAnswerTextKeyDown}
                    />

                    {/* Citations */}
                    {!!parsedAnswer.citations.length && (
                        <nav aria-label="Source citations">
                            <h4 className={styles.sectionHeader}>Citations</h4>
                            <div className={styles.citationsContainer}>
                                {parsedAnswer.citations.map((citation, i) => {
                                    // Parse citation to get sourceId, displayTitle, and url
                                    const { displayTitle, url } = parseCitation(citation, sourceUrls);
                                    const IconComponent = getFileIcon(displayTitle);
                                    return (
                                        <a key={i} href={url} target="_blank" rel="noopener noreferrer" className={styles.citationLink} title={displayTitle}>
                                            <span className={styles.citationNumber}>{i + 1}</span>
                                            <IconComponent size={18} weight="fill" className={styles.citationIcon} aria-hidden="true" />
                                            <span className={styles.citationText}>{insertWordBreaks(displayTitle)}</span>
                                        </a>
                                    );
                                })}
                            </div>
                        </nav>
                    )}

                    {/* Follow-up Questions */}
                    {!!parsedAnswer.followupQuestions.length && showFollowupQuestions && onFollowupQuestionClicked && (
                        <section aria-label="Suggested follow-up questions">
                            <h4 className={styles.sectionHeader}>Follow-up questions</h4>
                            <div className={styles.followupContainer}>
                                {parsedAnswer.followupQuestions.map((question, i) => (
                                    <button
                                        key={i}
                                        className={styles.followupQuestion}
                                        onClick={() => handleFollowupClick(question)}
                                        type="button"
                                        title={question}
                                    >
                                        {question}
                                    </button>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Typing indicator while streaming */}
                    {isStreaming && (
                        <div className={styles.typingIndicator} role="status"><span className={styles.srOnly}>Still generating response</span>
                            <span className={styles.typingDot} style={{ animationDelay: "0s" }} />
                            <span className={styles.typingDot} style={{ animationDelay: "0.15s" }} />
                            <span className={styles.typingDot} style={{ animationDelay: "0.3s" }} />
                        </div>
                    )}

                    {/* Rating — only shown when chat feedback is enabled */}
                    {onFeedbackClicked && (
                        <div className={styles.ratingContainer}>
                            <UserRating handleFeedback={onFeedbackClicked} handleAdditionalFeedback={onAdditionalFeedbackSubmitted} />
                        </div>
                    )}
                </div>
            </div>
        </article>
    );
};
